// Per-section bulk operations. Three locked actions per plan/02_review_ui.md:
//   - Accept all proposed (LLM-origin, pending) in the section
//   - Accept everything (all pending) in the section
//   - Reject all singletons (is_singleton + still pending) in the section

import { useMemo } from "react";
import type { GlossaryEntry } from "../data/types";
import { useReviewStore } from "./store";

interface Props {
  sectionId: string;
  entries: GlossaryEntry[];
}

export function BulkActions({ sectionId, entries }: Props) {
  const actions = useReviewStore((s) => s.actions);
  const bulkAccept = useReviewStore((s) => s.bulkAccept);
  const bulkReject = useReviewStore((s) => s.bulkReject);

  const { pendingLlmIds, allPendingIds, singletonIds } = useMemo(() => {
    const pendingLlmIds: string[] = [];
    const allPendingIds: string[] = [];
    const singletonIds: string[] = [];
    for (const e of entries) {
      const a = actions[e.term_id]?.action ?? "pending";
      if (a === "pending") {
        allPendingIds.push(e.term_id);
        if (e.origin === "llm") pendingLlmIds.push(e.term_id);
        if (e.is_singleton) singletonIds.push(e.term_id);
      }
    }
    return { pendingLlmIds, allPendingIds, singletonIds };
  }, [entries, actions]);

  return (
    <div className="maisight-review-bulk" data-section-id={sectionId} role="group" aria-label={`Bulk actions for ${sectionId}`}>
      <button
        type="button"
        className="maisight-review-btn maisight-review-btn--ghost"
        onClick={() => bulkAccept(pendingLlmIds)}
        disabled={pendingLlmIds.length === 0}
        aria-label={`Accept all proposed in ${sectionId}`}
      >
        Accept all proposed{pendingLlmIds.length > 0 ? ` (${pendingLlmIds.length})` : ""}
      </button>
      <button
        type="button"
        className="maisight-review-btn maisight-review-btn--ghost"
        onClick={() => bulkReject(singletonIds)}
        disabled={singletonIds.length === 0}
        aria-label={`Reject all singletons in ${sectionId}`}
        title={singletonIds.length ? `${singletonIds.length} singletons` : "No singletons in this section"}
      >
        Reject singletons{singletonIds.length > 0 ? ` (${singletonIds.length})` : ""}
      </button>
      <button
        type="button"
        className="maisight-review-btn maisight-review-btn--ghost"
        onClick={() => bulkAccept(allPendingIds)}
        disabled={allPendingIds.length === 0}
        aria-label={`Accept everything in ${sectionId}`}
      >
        Accept everything{allPendingIds.length > 0 ? ` (${allPendingIds.length})` : ""}
      </button>
    </div>
  );
}
